import type { CannedOriginDto } from "./cannedReportModel.ts";
import type { ReportAudienceDto } from "./reportSharingModel.ts";
// CRB5 (docs/build-briefs/CRB5-franchisor-custom-reports.md; design/mockups/report-builder/05-scope-franchisor-vs-franchisee): the pure
// decisions behind the franchisor console's custom report builder. No DOM, no fetch, nothing node-only, so a plain node:test file
// imports it (test/crb5-network-ui-model.test.ts) and the browser bundle does too.
//
// What it adds to the franchisee builder's model (customReportModel.ts, reused for the spec, the filters and the table):
//   * the permission reads (a gate resolves a permission id, never a role name);
//   * the two-level location tree, franchisee then its locations, with a tri-state per franchisee, a search, and the chip text;
//   * the date choices of a 366 day cap;
//   * a spec whose locations come from the tree (all of them, or the picked ids), and the way back from a saved spec;
//   * the saved list rows and the result's scope line.
// The server re-validates everything; nothing here decides what an account may see. The page holds no tenant id: the server sends
// an opaque franchisee reference and location ids only.

import type { CustomReportCatalogDto, CustomReportResultDto } from "./api.ts";
import {
  CR_CUSTOM_DATES,
  CR_PRESETS,
  buildSpec,
  freshBuilder,
  stateFromSpec,
  type BuilderState,
  type CatalogDataset,
  type SpecResult,
} from "./customReportModel.ts";

export const NETWORK_RUN_PERMISSION = "network.reports.run";
export const NETWORK_BUILD_PERMISSION = "network.reports.build";

/** Whether the account is offered the area at all: it may run reports. A permission read, never a role name. */
export function canOpenCustomReports(permissions: readonly string[]): boolean {
  return permissions.includes(NETWORK_RUN_PERMISSION);
}

/** Whether the account may build, save and archive. Running an unsaved report is building. */
export function canBuildCustomReports(permissions: readonly string[]): boolean {
  return permissions.includes(NETWORK_BUILD_PERMISSION);
}

export const CUSTOM_REPORTS_NAV = { id: "custom-reports", label: "Custom reports" } as const;
export const CUSTOM_REPORTS_TITLE = "Custom reports";
export const CUSTOM_REPORTS_FAILED = "The custom reports could not be loaded. Try again.";

/** All consoles use the same relative date choices. */
export const NETWORK_PRESETS = CR_PRESETS;

export interface NetworkCatalogDto extends CustomReportCatalogDto {
  franchisees: Array<{ ref: string; name: string; suspended?: true; locations: Array<{ id: string; name: string }> }>;
  canBuild: boolean;
}

export type NetworkResultDto = Omit<CustomReportResultDto, "meta"> & {
  meta: CustomReportResultDto["meta"] & { franchisees: number };
};

export interface NetworkSavedSummaryDto {
  id: string;
  title: string;
  dataset: string;
  datasetLabel: string;
  mine: boolean;
  createdBy: { name: string };
  currentVersion: number;
  editedAt: string;
  editedBy: { name: string };
  locations: { kind: "all" } | { kind: "list"; count: number };
  schedule?: {label:string;paused:boolean;nextAt?:string} | null;
}

export interface NetworkSavedListDto {
  items: NetworkSavedSummaryDto[];
  counts: { all: number; mine: number; scheduled?:number };
  locationCount: number;
}

export interface NetworkSavedDetailDto extends NetworkSavedSummaryDto {
  cannedOrigin?:CannedOriginDto|null;
  audience?:ReportAudienceDto;
  canShare?:boolean;
  spec: unknown;
  specHash: string;
  versions: Array<{
    cannedOrigin?:CannedOriginDto|null;
    specHash?:string;
    versionNumber: number;
    title: string;
    savedAt: string;
    savedBy: { name: string };
  }>;
}

export function isNetworkCatalogShape(body: unknown): body is NetworkCatalogDto {
  if (typeof body !== "object" || body === null) return false;
  const value = body as Partial<NetworkCatalogDto>;
  return (
    Array.isArray(value.catalog?.datasets) &&
    Array.isArray(value.franchisees) &&
    Array.isArray(value.locations) &&
    typeof value.businessDate === "string" &&
    typeof value.canBuild === "boolean"
  );
}

// ---------------------------------------------------------------------
// The location tree (mockup 05)
// ---------------------------------------------------------------------

export type TreeFranchisee = NetworkCatalogDto["franchisees"][number];

/** The locations a person picked, by id. The tree itself is the catalog's. */
export type LocationSelection = ReadonlySet<string>;

export function allLocationIds(tree: readonly TreeFranchisee[]): string[] {
  return tree.flatMap((f) => f.locations.map((l) => l.id));
}

export function selectAll(tree: readonly TreeFranchisee[]): LocationSelection {
  return new Set(allLocationIds(tree));
}

export type TriState = "on" | "off" | "mixed";

/** A franchisee's check: on when every one of its locations is picked, off when none is, mixed otherwise. */
export function triStateOf(franchisee: TreeFranchisee, selection: LocationSelection): TriState {
  const picked = franchisee.locations.filter((l) => selection.has(l.id)).length;
  if (picked === 0) return "off";
  return picked === franchisee.locations.length ? "on" : "mixed";
}

export function toggleLocation(selection: LocationSelection, id: string): LocationSelection {
  const next = new Set(selection);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  return next;
}

/** Tapping a franchisee's check: a mixed or off franchisee becomes fully on; an on one becomes off. */
export function toggleFranchisee(
  selection: LocationSelection,
  franchisee: TreeFranchisee,
): LocationSelection {
  const next = new Set(selection);
  const turnOff = triStateOf(franchisee, selection) === "on";
  for (const location of franchisee.locations) {
    if (turnOff) next.delete(location.id);
    else next.add(location.id);
  }
  return next;
}

/** "9 locations in 5 franchisees" for the whole tree, or "6 of 9 in 4 franchisees" for a pick. */
export function selectionSummary(
  tree: readonly TreeFranchisee[],
  selection: LocationSelection,
): string {
  const total = allLocationIds(tree).length;
  const franchisees = tree.filter((f) => f.locations.some((l) => selection.has(l.id))).length;
  const picked = tree.reduce(
    (n, f) => n + f.locations.filter((l) => selection.has(l.id)).length,
    0,
  );
  if (picked === 0) return "None";
  if (picked === total) return `All ${total} ${total === 1 ? "location" : "locations"}`;
  return `${picked} of ${total} in ${franchisees} ${franchisees === 1 ? "franchisee" : "franchisees"}`;
}

/** The tree for a search: a franchisee whose name matches keeps all its locations; otherwise only the locations that match. */
export function filterTree(tree: readonly TreeFranchisee[], query: string): TreeFranchisee[] {
  const needle = query.trim().toLowerCase();
  if (needle === "") return [...tree];
  const out: TreeFranchisee[] = [];
  for (const franchisee of tree) {
    if (franchisee.name.toLowerCase().includes(needle)) {
      out.push(franchisee);
      continue;
    }
    const locations = franchisee.locations.filter((l) => l.name.toLowerCase().includes(needle));
    if (locations.length > 0) out.push({ ...franchisee, locations });
  }
  return out;
}

/** The spec's `locations`: every location of the network (kept as "all", so a location opened later is included) or the picked ids. */
export function specLocations(
  tree: readonly TreeFranchisee[],
  selection: LocationSelection,
): { kind: "all_permitted" } | { kind: "list"; ids: string[] } | undefined {
  const ids = allLocationIds(tree).filter((id) => selection.has(id));
  if (ids.length === 0) return undefined;
  return ids.length === allLocationIds(tree).length
    ? { kind: "all_permitted" }
    : { kind: "list", ids };
}

export const NO_LOCATIONS_PICKED = "Pick at least one location.";

/** The version 1 spec for the builder's choices over the tree's selection, or one sentence saying what is missing. */
export function buildNetworkSpec(
  state: BuilderState,
  dataset: CatalogDataset,
  tree: readonly TreeFranchisee[],
  selection: LocationSelection,
  datasets?: CatalogDataset[],
): SpecResult {
  const locations = specLocations(tree, selection);
  if (locations === undefined) return { ok: false, message: NO_LOCATIONS_PICKED };
  const built = buildSpec(state, dataset, datasets);
  if (!built.ok) return built;
  return { ok: true, spec: { ...built.spec, locations } };
}

function isObj(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * The builder's choices and the location selection for a saved spec, so a report can be edited as a new version. Undefined when
 * the builder cannot hold the spec exactly: the screen then offers no Edit rather than quietly changing what the report does.
 * A saved list that names a location no longer in the tree is also undefined (it would be run and refused, not shrunk).
 */
export function networkStateFromSpec(
  raw: unknown,
  dataset: CatalogDataset,
  tree: readonly TreeFranchisee[],
  datasets?: CatalogDataset[],
): { state: BuilderState; selection: LocationSelection } | undefined {
  if (!isObj(raw)) return undefined;
  const range = raw["dateRange"];
  const extraPreset =
    isObj(range) &&
    range["kind"] === "relative" &&
    typeof range["preset"] === "string" &&
    NETWORK_PRESETS.some((p) => p.id === range["preset"]) &&
    !CR_PRESETS.some((p) => p.id === range["preset"])
      ? (range["preset"] as string)
      : undefined;
  const locations = raw["locations"];
  let selection: LocationSelection;
  if (!isObj(locations)) return undefined;
  if (locations["kind"] === "all_permitted") {
    selection = selectAll(tree);
  } else if (
    locations["kind"] === "list" &&
    Array.isArray(locations["ids"]) &&
    locations["ids"].length > 0
  ) {
    const known = new Set(allLocationIds(tree));
    if (!locations["ids"].every((id) => typeof id === "string" && known.has(id))) return undefined;
    selection = new Set(locations["ids"] as string[]);
  } else {
    return undefined;
  }
  // The franchisee model parses everything but the long presets and a location list; hand it a spec it can hold, then restore.
  const state = stateFromSpec(
    {
      ...raw,
      locations: { kind: "all_permitted" },
      ...(extraPreset === undefined
        ? {}
        : { dateRange: { kind: "relative", preset: "last_30_days" } }),
    },
    dataset,
    datasets,
  );
  if (state === undefined) return undefined;
  if (extraPreset !== undefined) state.dates = extraPreset;
  state.location = "all";
  return { state, selection };
}

export function freshNetworkBuilder(dataset: string): BuilderState {
  return freshBuilder(dataset);
}

export { CR_CUSTOM_DATES };

// ---------------------------------------------------------------------
// Words on the screen
// ---------------------------------------------------------------------

/** "4 franchisees, 9 locations" under the result's title. */
export function scopeText(result: NetworkResultDto): string {
  const f = result.meta.franchisees;
  const l = result.meta.locations.length;
  return `${f} ${f === 1 ? "franchisee" : "franchisees"}, ${l} ${l === 1 ? "location" : "locations"}`;
}

/** The Locations cell of a saved list row. */
export function savedLocationsText(row: NetworkSavedSummaryDto, total: number): string {
  if (row.locations.kind === "all") return `All ${total}`;
  return `${row.locations.count} of ${total}`;
}

export const SAVED_EMPTY = "No saved reports yet.";
export const SAVED_NONE_MATCH = "No saved reports match.";

/** Saved and scheduled reports visible to the current account. */
export function savedTabs(
  list: NetworkSavedListDto,
): Array<{ id: "all" | "mine" | "scheduled"; label: string; count: number }> {
  return [
    { id: "all", label: "All", count: list.counts.all },
    { id: "mine", label: "Mine", count: list.counts.mine },
    { id: "scheduled", label: "Scheduled", count: list.counts.scheduled ?? 0 },
  ];
}

/** Whether Export shows for a result on screen: it ran, and an unsaved report needs build, a saved one needs run. */
export function mayExportNetwork(
  permissions: readonly string[],
  hasRun: boolean,
  saved: boolean,
): boolean {
  return hasRun && permissions.includes(saved ? NETWORK_RUN_PERMISSION : NETWORK_BUILD_PERMISSION);
}

/** The file name a download is given, matching the server's. */
export function networkExportFileName(
  result: Pick<NetworkResultDto, "meta">,
  format: "csv" | "xlsx" | "pdf",
): string {
  return `custom-report_${result.meta.from}_${result.meta.to}.${format}`;
}
